import { useMemo, useState, type FormEvent } from "react";
import { Loader2, Search, Trash2, ExternalLink, Building2, Pencil, MoreVertical } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { formatDateBR, formatLocalTimeHHmm, parseISODateOrDateTime, toISODateOnlyLocal } from "@/lib/date-utils";
import { Button } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { normalizeStoragePath } from "@/lib/utils/storage";
import { DocumentDeleteConfirmDialog } from "@/components/pets/DocumentDeleteConfirmDialog";
import { DocumentEditDialog } from "@/components/pets/DocumentEditDialog";
import { useTranslation } from "react-i18next";

export type AttachmentRow = Database["public"]["Tables"]["pet_attachments"]["Row"] & {
  clinics?: {
    id: string;
    name: string | null;
  } | null;
};

interface ExamGalleryProps {
  attachments: AttachmentRow[];
  isLoading?: boolean;
  onDelete?: (attachment: AttachmentRow) => Promise<void>;
  onUpdate?: (payload: {
    attachmentId: string;
    name?: string | null;
    type: string;
    description: string | null;
    examDate: string | null;
    occurredAt?: string | null;
  }) => Promise<void>;
  readOnly?: boolean;
}

const filterAttachment = (attachment: AttachmentRow, search: string) => {
  const fileName = attachment.file_name || attachment.original_name || attachment.filename || "";
  const title = attachment.file_name || "";
  const type = attachment.type || attachment.category || "";
  const description = attachment.description || "";
  const matchesSearch =
    !search ||
    fileName.toLowerCase().includes(search.toLowerCase()) ||
    title.toLowerCase().includes(search.toLowerCase()) ||
    type.toLowerCase().includes(search.toLowerCase()) ||
    description.toLowerCase().includes(search.toLowerCase());
  return matchesSearch;
};

export const ExamGallery = ({
  attachments,
  isLoading,
  onDelete,
  onUpdate,
  readOnly,
}: ExamGalleryProps) => {
  const { t } = useTranslation("medicalRecords");
  const { data: currentClinicId } = useCurrentClinicId();
  const [searchTerm, setSearchTerm] = useState("");
  const [attachmentToEdit, setAttachmentToEdit] = useState<AttachmentRow | null>(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState<AttachmentRow | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftType, setDraftType] = useState("");
  const [draftDate, setDraftDate] = useState("");
  const [draftTime, setDraftTime] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const filtered = useMemo(
    () => attachments.filter((attachment) => filterAttachment(attachment, searchTerm)),
    [attachments, searchTerm],
  );

  const getFileBaseName = (fileName: string) => {
    const trimmed = fileName.trim();
    if (!trimmed) return "";
    const lastDot = trimmed.lastIndexOf(".");
    if (lastDot <= 0) return trimmed;
    return trimmed.slice(0, lastDot);
  };

  const getAttachmentTitle = (attachment: AttachmentRow) => {
    if (attachment.file_name) return attachment.file_name;
    const fallbackName =
      attachment.original_name || attachment.filename || "";
    return getFileBaseName(fallbackName) || t("gallery.untitledDocument");
  };

  const getAttachmentType = (attachment: AttachmentRow) =>
    attachment.type || attachment.category || "";

  const getAttachmentDateTime = (attachment: AttachmentRow) =>
    parseISODateOrDateTime(attachment.created_at) ??
    (attachment.exam_date ? parseISODateOrDateTime(attachment.exam_date) : null) ??
    new Date();

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSearchTerm((term) => term.trim());
  };

  const handleEditStart = (attachment: AttachmentRow) => {
    const documentDateTime = getAttachmentDateTime(attachment);
    setAttachmentToEdit(attachment);
    setDraftTitle(getAttachmentTitle(attachment));
    setDraftType(getAttachmentType(attachment));
    setDraftDate(toISODateOnlyLocal(documentDateTime));
    setDraftTime(formatLocalTimeHHmm(documentDateTime));
    setDraftDescription(attachment.description ?? "");
  };

  const handleEditCancel = () => {
    setAttachmentToEdit(null);
    setDraftTitle("");
    setDraftType("");
    setDraftDate("");
    setDraftTime("");
    setDraftDescription("");
  };

  const handleEditSave = async (attachment: AttachmentRow) => {
    if (!onUpdate) return;
    const nextTitle = draftTitle.trim();
    if (!nextTitle) {
      toast.error(t("gallery.errors.missingName"));
      return;
    }
    const nextType = draftType.trim();
    if (!nextType) {
      toast.error(t("gallery.errors.missingType"));
      return;
    }
    if (!draftDate || !draftTime) {
      toast.error(t("gallery.errors.missingDateTime"));
      return;
    }
    const occurredAt = new Date(`${draftDate}T${draftTime}`);
    if (Number.isNaN(occurredAt.getTime())) {
      toast.error(t("gallery.errors.invalidDateTime"));
      return;
    }
    setIsSaving(true);
    try {
      await onUpdate({
        attachmentId: attachment.id,
        name: nextTitle,
        type: nextType,
        description: draftDescription.trim() ? draftDescription.trim() : null,
        examDate: draftDate,
        occurredAt: occurredAt.toISOString(),
      });
      handleEditCancel();
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenFile = async (attachment: AttachmentRow) => {
    try {
      const storagePath =
        normalizeStoragePath(attachment.file_path, attachment.storage_bucket) ??
        attachment.file_path;

      if (storagePath.startsWith("http")) {
        window.open(storagePath, "_blank");
        return;
      }

      const { data, error } = await supabase.storage
        .from(attachment.storage_bucket ?? "pet-attachments")
        .createSignedUrl(storagePath, 3600); // 1 hora de validade

      if (error) {
        toast.error(t("gallery.errors.openFailed"));
        return;
      }

      // Abrir em nova aba
      window.open(data.signedUrl, "_blank");
    } catch (err) {
      console.error("Erro ao abrir arquivo:", err);
      toast.error(t("gallery.errors.openFailed"));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!attachmentToDelete || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(attachmentToDelete);
      setAttachmentToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="rounded-lg border bg-background">
      <div className=" px-4 py-3">
        <h3 className="text-lg font-semibold">{t("gallery.title")}</h3>
      </div>
      <div className="space-y-4 p-4 pt-2">
        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-center"
          onSubmit={handleSearchSubmit}
        >
          <Input
            placeholder={t("gallery.searchPlaceholder")}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="flex-1"
          />
          <Button type="submit" variant="outline" size="sm" className="gap-2">
            <Search className="h-4 w-4" />
            {t("gallery.search")}
          </Button>
        </form>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("gallery.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {searchTerm ? t("gallery.emptySearch") : t("gallery.empty")}
          </p>
        ) : (
          <div className="max-h-[360px] space-y-3 overflow-y-auto">
            {filtered.map((attachment) => {
              const title = getAttachmentTitle(attachment);
              const documentType = getAttachmentType(attachment);
              const documentDate = attachment.exam_date
                ? formatDateBR(attachment.exam_date)
                : attachment.created_at
                  ? formatDateBR(attachment.created_at)
                  : null;
              return (
                <div key={attachment.id} className="group rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold">{title}</p>
                          {documentType ? (
                            <Badge variant="secondary">{documentType}</Badge>
                          ) : null}
                          {attachment.clinics && currentClinicId && attachment.clinics.id !== currentClinicId && (
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {attachment.clinics.name}
                            </Badge>
                          )}
                        </div>
                        {documentDate && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("gallery.documentDateLabel", { date: documentDate })}
                          </p>
                        )}
                      </div>
                      {attachment.description ? (
                        <>
                          <Separator />
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {attachment.description}
                          </p>
                        </>
                      ) : null}
                    </div>
                    <div className="flex md:flex-row flex-col items-center gap-2">
                      <div onClick={(event) => event.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreVertical className="h-4 w-4" />
                              <span className="sr-only">{t("gallery.actionsMenu")}</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => handleOpenFile(attachment)}>
                              <ExternalLink className="mr-2 h-4 w-4" />
                              {t("gallery.open")}
                            </DropdownMenuItem>
                            {!readOnly && onUpdate ? (
                              <DropdownMenuItem onSelect={() => handleEditStart(attachment)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                {t("gallery.edit")}
                              </DropdownMenuItem>
                            ) : null}
                            {!readOnly && onDelete ? (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onSelect={() => setAttachmentToDelete(attachment)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {t("gallery.delete")}
                                </DropdownMenuItem>
                              </>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <DocumentEditDialog
        open={Boolean(attachmentToEdit)}
        onOpenChange={(open) => {
          if (!open) handleEditCancel();
        }}
        labels={{
          title: t("gallery.editDocument"),
          documentName: t("gallery.documentName"),
          documentNamePlaceholder: t("gallery.editTitlePlaceholder"),
          documentDate: t("gallery.documentDate"),
          documentTime: t("gallery.documentTime"),
          observations: t("gallery.observations"),
          observationsPlaceholder: t("gallery.editDescriptionPlaceholder"),
          cancel: t("gallery.cancel"),
          save: t("gallery.save"),
          saving: t("gallery.saving"),
        }}
        name={draftTitle}
        onNameChange={setDraftTitle}
        type={draftType}
        onTypeChange={setDraftType}
        date={draftDate}
        onDateChange={setDraftDate}
        time={draftTime}
        onTimeChange={setDraftTime}
        description={draftDescription}
        onDescriptionChange={setDraftDescription}
        onSave={() => {
          if (attachmentToEdit) {
            void handleEditSave(attachmentToEdit);
          }
        }}
        isSaving={isSaving}
      />
      <DocumentDeleteConfirmDialog
        open={Boolean(attachmentToDelete)}
        onOpenChange={(open) => {
          if (!open) setAttachmentToDelete(null);
        }}
        labels={{
          title: t("gallery.deleteDocumentTitle"),
          description: t("gallery.deleteDocumentConfirmation"),
          cancel: t("gallery.cancel"),
          confirm: t("gallery.delete"),
          confirming: t("gallery.deleting"),
        }}
        onConfirm={() => {
          void handleDeleteConfirm();
        }}
        isDeleting={isDeleting}
      />
    </div>
  );
};
